import type { MenuItem } from '@/consts/navigation';
import { cn } from '@/utils/cn';
import { ChevronRight } from 'lucide-react';

import { useNavigationContext } from '../context/NavigationContext';
import { useActiveMenuItem } from '../hooks/useActiveMenuItem';
import { NavBadge } from './NavBadge';
import { NavMenuIcon } from './NavMenuIcon';

interface NavExpandedContentProps {
  item: MenuItem;
  translatedText: string;
}

export const NavExpandedContent = ({ item, translatedText }: NavExpandedContentProps) => {
  const { expandedSections } = useNavigationContext();
  const { isDirectlyActive, hasActiveChildren } = useActiveMenuItem(item);

  const hasChildren = Boolean(item.children?.length);
  const sectionKey = (item.code as string) || item.text;
  const isExpanded = expandedSections.has(sectionKey);
  const isActive = isDirectlyActive || hasActiveChildren;

  return (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        <span
          className={cn(
            'flex-shrink-0',
            isActive ? 'text-primary-100' : 'text-nav-item-icon group-hover:text-nav-item'
          )}
        >
          <NavMenuIcon item={item} />
        </span>
        <span className="truncate text-left text-[13px] leading-tight">{translatedText}</span>
      </div>

      <NavBadge badge={item.badge} />

      {hasChildren && (
        <ChevronRight
          className={cn(
            'text-nav-item-icon h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200',
            isExpanded && 'rotate-90'
          )}
          aria-hidden="true"
        />
      )}
    </>
  );
};
